/**
 * useLivery - the root element wears the phase and, where the phase follows
 * it, the danger step or Stealth's rung.
 *
 * `document.documentElement` carries `data-phase`, `data-danger`,
 * `data-rung` and (a viewer's own setting) `data-stealth-floor`, which is
 * all `styles/livery.css` reads. On the ROOT rather than the shell's own
 * element so that dialogs, menus and toasts rendered outside it wear the
 * same livery. Both are removed when the phase is unknown and when the
 * simulator unmounts (the lobby wears the plain theme).
 *
 * @param {readonly import('../engine/roster/types').Combatant[]} roster
 * @param {import('./useDirection').Direction} direction
 * @returns {import('../engine/phase/danger').DangerReading} who stands against the party, and the step
 */
import { useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import { getViewerPrefs, subscribeViewerPrefs } from '../utils/viewerPrefs';
import { readDanger } from '../engine/phase/danger';
import { crossingBetween } from '../engine/phase/transitions';
import { crossFade } from '../utils/crossFade';
import useHoldsStill from './useHoldsStill';
import { layoutOf } from '../engine/phase/layout';
import { emptyFrame, stealthOf } from '../engine/phase/frame';
import { wear } from '../utils/rootAttribute';

const NO_FRAME = emptyFrame();

export default function useLivery(roster, direction) {
  const { shown, frame } = direction;
  const danger = useMemo(() => readDanger(roster, frame ?? NO_FRAME), [roster, frame]);
  const step = shown && layoutOf(shown).wearsDanger ? danger.step : null;
  const rung = shown && layoutOf(shown).wearsRung ? stealthOf(frame).rung : null;
  // The viewer's own floor under Stealth's dark (a private setting): livery.css lifts the blacks.
  const prefs = useSyncExternalStore(subscribeViewerPrefs, getViewerPrefs, getViewerPrefs);
  const floor = prefs.stealthBrightnessFloor === true ? 'raised' : null;
  // No cross-fade while the screen holds still.
  const still = useHoldsStill();
  // What the root wore last, to tell which crossing this is (engine/phase/transitions).
  const worn = useRef({ phase: null, rung: null });
  useEffect(() => {
    const now = { phase: shown, rung };
    const crossing = still ? null : crossingBetween(worn.current, now);
    worn.current = now;
    // A change of phase, or the alarm: the whole screen cross-fades. Anything else is put on at once,
    // and the ground eases by itself.
    crossFade(crossing?.fadeMs ?? 0, () => {
      wear('phase', shown);
      wear('danger', step);
      wear('rung', rung);
      wear('stealthFloor', floor);
    });
  }, [shown, step, rung, floor, still]);
  useEffect(
    () => () => {
      wear('phase', null);
      wear('danger', null);
      wear('rung', null);
      wear('stealthFloor', null);
    },
    [],
  );
  return danger;
}
